import React, { useMemo } from 'react';
import { convertNumberToWords, formatNumberWithCommas, getAbbreviationForPi } from '../../utilities';

const formatDate = (timestamp) =>
  new Date(timestamp * 1000)
    .toLocaleString('en-GB', {
      timeZone: 'Asia/Dhaka',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
    .replace(/\//g, '.');

const isBlank = (value) => value === null || value === undefined || String(value).trim() === '';

// Distinct, non-blank item styles across the whole commercial (all orders/items),
// in order of first appearance. Independent of which GSMs are flagged for display.
const get_available_styles = (table_info) => {
  const seen = new Set();
  const styles = [];
  table_info.forEach((order) => {
    order.items.forEach((item) => {
      const style = (item.item_style || '').trim();
      if (style && !seen.has(style)) {
        seen.add(style);
        styles.push(style);
      }
    });
  });
  return styles;
};

// Keep only GSMs flagged for commercial display, and drop any
// specification/item/order that ends up with nothing left to show.
const filter_for_commercial_display = (table_info) => {
  return table_info
    .map((order) => {
      const items = order.items
        .map((item) => {
          const specifications = item.specifications
            .map((spec) => ({
              ...spec,
              gsms: spec.gsms.filter((gsm) => gsm.commercialMeasurement),
            }))
            .filter((spec) => spec.gsms.length > 0);
          return { ...item, specifications };
        })
        .filter((item) => item.specifications.length > 0);
      return { ...order, items };
    })
    .filter((order) => order.items.length > 0);
};

// Generic inline-editable wrapper. `as` lets you pick the tag (span/div/td/etc).
const EditableField = ({ editMode, as: Tag = 'span', style, children, ...rest }) => (
  <Tag
    contentEditable={editMode}
    suppressContentEditableWarning={true}
    style={{
      outline: editMode ? '1px dashed #3b82f6' : 'none',
      backgroundColor: editMode ? 'rgba(59,130,246,0.06)' : 'transparent',
      minWidth: editMode ? '10px' : undefined,
      cursor: editMode ? 'text' : 'inherit',
      ...style,
    }}
    {...rest}
  >
    {children}
  </Tag>
);

// Defined outside the page component on purpose: a component created inside it is a new type on
// every render, so React would rebuild these rows from the data and wipe whatever was typed into
// them (e.g. when Save drops out of edit mode just before capturing the page).
const Row = ({ editMode, label, children }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '2px' }}>
    <EditableField editMode={editMode} as="div" style={{ width: '160px', flexShrink: 0, fontWeight: 'bold' }}>
      {label}
    </EditableField>
    <div style={{ width: '15px', flexShrink: 0 }}>:</div>
    <EditableField editMode={editMode} as="div" style={{ flex: 1 }}>
      {children}
    </EditableField>
  </div>
);

const CommercialInvoice = ({ commercial_info, table_info, editMode = false }) => {

  const display_table_info = useMemo(
    () => filter_for_commercial_display(table_info),
    [table_info]
  );

  // Available styles come from the full (unfiltered) item table for this commercial —
  // used only as a fallback (see all_measurements_selected below).
  const available_styles = useMemo(() => get_available_styles(table_info), [table_info]);

  // True only when every GSM line in this commercial is flagged for display. In that
  // (normal) case each item shows its own style next to its name, same as before the
  // merged Style column existed. As soon as any line is unchecked, an item could
  // otherwise lose its style entirely (if all of that item's lines get hidden), so we
  // fall back to one merged cell listing every style in the document instead.
  const all_measurements_selected = useMemo(
    () =>
      table_info.every((order) =>
        order.items.every((item) =>
          item.specifications.every((spec) => spec.gsms.every((gsm) => gsm.commercialMeasurement))
        )
      ),
    [table_info]
  );

  // Column width budget (% of table, always totals 100). Style sits in a different position in the
  // two layouts, and the merged layout holds the whole style list in one cell, so it gets more room.
  const col_w = all_measurements_selected
    ? { buyer: '10%', item: '18%', style: '18%', ply: '7%', meas: '14%', qty: '11%', unit: '10%', total: '12%' }
    : { buyer: '9%', item: '16%', style: '21%', ply: '7%', meas: '14%', qty: '11%', unit: '10%', total: '12%' };

  // Total number of data rows in the table body, so the Style cell can span all of them.
  const grandRowSpanLength = display_table_info.reduce((acc, order) => {
    return acc + order.items.reduce((itemAcc, item) => {
      return itemAcc + item.specifications.reduce((specAcc, spec) => specAcc + spec.gsms.length, 0);
    }, 0);
  }, 0);

  // Grand total of Qty and Amount, based on the commercial (display) values
  const { grandQty, grandAmount } = display_table_info.reduce(
    (acc, order) => {
      order.items.forEach((item) => {
        item.specifications.forEach((spec) => {
          spec.gsms.forEach((gsm) => {
            const qty = Number(gsm.commercialQty) || 0;
            const unitPrice = Number(gsm.commercialUnitPrice) || 0;
            acc.grandQty += qty;
            acc.grandAmount += qty * unitPrice;
          });
        });
      });
      return acc;
    },
    { grandQty: 0, grandAmount: 0 }
  );

  // Amendment fields for BTB L/C NO row
  const amendmentFields = [
    { label: 'Number of Amendment:', value: commercial_info.amendmentInfo },
    {
      label: 'DATED:',
      value: !isBlank(commercial_info.amendmentDate) ? formatDate(commercial_info.amendmentDate) : null,
    },
  ].filter((field) => !isBlank(field.value));

  // Everything from HS CODE NO through BENEFICIARY BIN for EXPORT S/C NO row
  // (contract no / date are handled inline, before this list)
  const exportScFields = [
    { label: 'HS CODE NO:', value: '4819.10.00' },
    { label: 'APPLICANT BIN:', value: commercial_info.partyBin },
    { label: 'APPLICANT BOND LIC. NO.:', value: commercial_info.partyBondLicNo },
    { label: 'APPLICANT BOI NO.:', value: commercial_info.partyBoiNo },
    { label: 'BANK BIN:', value: commercial_info.partyBankBin },
    { label: 'APPLICANT ERC:', value: commercial_info.partyErc },
    { label: 'APPLICANT IRC:', value: commercial_info.partyIrc },
    { label: 'BENEFICIARY BIN:', value: '000686131-1205' },
  ].filter((field) => !isBlank(field.value));

  const netWeightFields = [
  { label: 'NET WEIGHT:', value: commercial_info.netWeight, suffix: ' KGS' },
  { label: 'GROSS WEIGHT:', value: commercial_info.grossWeight, suffix: ' KGS' },
  { label: 'BUNDLE:', value: commercial_info.bundle, suffix: '' },
  ].filter((field) => !isBlank(field.value) && Number(field.value) !== 0);

  return (
    <div style={{ display: 'block', fontSize: '13px' }}>
      <style>{`
        @media print {
          [contenteditable] {
            outline: none !important;
            background-color: transparent !important;
          }
        }
      `}</style>

      <div style={{ textAlign: 'center' }}>
        <EditableField
          editMode={editMode}
          as="h1"
          style={{
            textDecoration: 'underline',
            fontWeight: 'bold',
            marginTop: '5px',
            marginBottom: '15px',
            fontSize: '23px',
            color: '#000',
            display: 'inline-block',
          }}
        >
          COMMERCIAL INVOICE
        </EditableField>
      </div>

      <div style={{ fontSize: '13px', lineHeight: '18px' }}>
        <Row editMode={editMode} label="INVOICE NO">
          {commercial_info.invoiceNo}, DATE-{formatDate(commercial_info.commercialDate)}
        </Row>

        <Row editMode={editMode} label="EXPORTED/SENDER BY">
          M/S MIAH & SONS INDUSTRIES LIMITED110/1, BARABO, KALIAKOIR, GAZIPUR, BANGLADESH.
        </Row>

        <Row editMode={editMode} label="SHIPPED/DELIVERED BY">TRUCK FROM OUR FACTORY GO-DOWN TO YOUR FACTORY SITE</Row>

        <Row editMode={editMode} label="FOR ACCOUNT & RISK OF">
          {commercial_info.partyName}
          <br />
          {commercial_info.partyAddress}
        </Row>

        <Row editMode={editMode} label="BTB L/C NO">
          {commercial_info.lcNo}, DATED: {formatDate(commercial_info.lcDate)}
          {amendmentFields.length > 0 && ', '}
          {amendmentFields.map((field, index) => (
            <React.Fragment key={field.label}>
              {field.label} {field.value}
              {index < amendmentFields.length - 1 ? ', ' : ''}
            </React.Fragment>
          ))}.
        </Row>

        <Row editMode={editMode} label="EXPORT S/C NO">
          {commercial_info.contractNo} DT.{formatDate(commercial_info.contractDate)}
          {exportScFields.length > 0 && ', '}
          {exportScFields.map((field, index) => (
            <React.Fragment key={field.label}>
              {field.label}{field.value}
              {index < exportScFields.length - 1 ? ', ' : ''}
            </React.Fragment>
          ))}.
        </Row>

        <Row editMode={editMode} label="PI NO">
          {commercial_info.piInfo.map((pi, index) => (
            <span key={pi.piId}>
              {pi.piNo || `PI/${getAbbreviationForPi(commercial_info.partyName)}/${pi.jobNumber}`} DT.{formatDate(pi.createdDate)}
              {index < commercial_info.piInfo.length - 1 ? ', ' : ''}
            </span>
          ))}.
        </Row>

        <Row editMode={editMode} label="TRADE TERM">
          CPT APPLICANT'S FACTORY (INCOTERM 2020)
        </Row>

        <Row editMode={editMode} label="NOTIFIED TO">
          {commercial_info.partyBank},<br />
          {commercial_info.partyBankAddress}
        </Row>
      </div>

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        className="cmp-table"
        style={{ border: '1px solid #000', marginTop: '20px', marginBottom: '15px', tableLayout: 'fixed' }}
        fontSize="13px"
      >
        <thead>
          <tr>
            <th style={{ width: col_w.buyer, boxSizing: 'border-box', border: '1px solid black', padding: '8px 10px', backgroundColor: 'rgba(0, 0, 0, 0.05)', textAlign: 'center' }}>Buyer Name</th>
            {!all_measurements_selected && (
              <th style={{ width: col_w.style, boxSizing: 'border-box', border: '1px solid black', padding: '8px 10px', backgroundColor: 'rgba(0, 0, 0, 0.05)', textAlign: 'center' }}>Style</th>
            )}
            <th style={{ width: col_w.item, boxSizing: 'border-box', border: '1px solid black', padding: '8px 10px', backgroundColor: 'rgba(0, 0, 0, 0.05)', textAlign: 'center' }}>Item Name</th>
            {all_measurements_selected && (
              <th style={{ width: col_w.style, boxSizing: 'border-box', border: '1px solid black', padding: '8px 10px', backgroundColor: 'rgba(0, 0, 0, 0.05)', textAlign: 'center' }}>Style</th>
            )}
            <th style={{ width: col_w.ply, boxSizing: 'border-box', border: '1px solid black', padding: '8px 10px', backgroundColor: 'rgba(0, 0, 0, 0.05)', textAlign: 'center' }}>PLY</th>
            <th style={{ width: col_w.meas, boxSizing: 'border-box', border: '1px solid black', padding: '8px 10px', backgroundColor: 'rgba(0, 0, 0, 0.05)', textAlign: 'center' }}>Measurement</th>
            <th style={{ width: col_w.qty, boxSizing: 'border-box', border: '1px solid black', padding: '8px 10px', backgroundColor: 'rgba(0, 0, 0, 0.05)', textAlign: 'center' }}>Qty</th>
            <th style={{ width: col_w.unit, boxSizing: 'border-box', border: '1px solid black', padding: '8px 10px', backgroundColor: 'rgba(0, 0, 0, 0.05)', textAlign: 'center' }}>Unit Price</th>
            <th style={{ width: col_w.total, boxSizing: 'border-box', border: '1px solid black', padding: '8px 10px', backgroundColor: 'rgba(0, 0, 0, 0.05)', textAlign: 'center' }}>Total Amount</th>
          </tr>
        </thead>
        <tbody>
          {display_table_info.map((order, orderIndex) => {
            const orderRowSpanLength = order.items.reduce((acc, item) => {
              return acc + item.specifications.reduce((specAcc, spec) => specAcc + spec.gsms.length, 0);
            }, 0);

            const orderRows = order.items.map((item, itemIndex) => {
              const itemRowSpanLength = item.specifications.reduce((acc, spec) => acc + spec.gsms.length, 0);

              return item.specifications.map((spec, specIndex) =>
                spec.gsms.map((gsm, gsmIndex) => {
                  const qty = Number(gsm.commercialQty) || 0;
                  const unitPrice = Number(gsm.commercialUnitPrice) || 0;
                  const lineTotal = qty * unitPrice;

                  return (
                  <tr key={`${orderIndex}-${itemIndex}-${specIndex}-${gsmIndex}`}>
                    {itemIndex === 0 && specIndex === 0 && gsmIndex === 0 && (
                      <td rowSpan={orderRowSpanLength} style={{ border: '1px solid black', padding: '0', textAlign: 'center', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        <EditableField editMode={editMode} as="div" style={{ padding: '6px 10px', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                          {order.buyer_name}
                        </EditableField>
                      </td>
                    )}
                    {!all_measurements_selected && orderIndex === 0 && itemIndex === 0 && specIndex === 0 && gsmIndex === 0 && (
                      <td rowSpan={grandRowSpanLength} style={{ border: '1px solid black', padding: '0', textAlign: 'center', verticalAlign: 'middle', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        <EditableField editMode={editMode} as="div" style={{ padding: '6px 10px', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                          {available_styles.map((style, styleIndex) => (
                            <React.Fragment key={style}>
                              {style}
                              {styleIndex < available_styles.length - 1 && <br />}
                            </React.Fragment>
                          ))}
                        </EditableField>
                      </td>
                    )}
                    {specIndex === 0 && gsmIndex === 0 && (
                      <td rowSpan={itemRowSpanLength} style={{ border: '1px solid black', padding: '0', textAlign: 'center', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        <EditableField editMode={editMode} as="div" style={{ padding: '6px 10px', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                          {item.item_name}
                        </EditableField>
                      </td>
                    )}
                    {all_measurements_selected && specIndex === 0 && gsmIndex === 0 && (
                      <td rowSpan={itemRowSpanLength} style={{ border: '1px solid black', padding: '0', textAlign: 'center', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        <EditableField editMode={editMode} as="div" style={{ padding: '6px 10px', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                          {item.item_style}
                        </EditableField>
                      </td>
                    )}
                    <td style={{ border: '1px solid black', padding: '0', textAlign: 'center', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      <EditableField editMode={editMode} as="div" style={{ padding: '6px 10px', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        {spec.specification}
                      </EditableField>
                    </td>
                    <td style={{ border: '1px solid black', padding: '0', textAlign: 'center', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      <EditableField editMode={editMode} as="div" style={{ padding: '6px 10px', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        {gsm.measurement.split('\n').map((line, lineIndex) => (
                          <React.Fragment key={lineIndex}>
                            {line}
                            <br />
                          </React.Fragment>
                        ))}
                      </EditableField>
                    </td>
                    <td style={{ border: '1px solid black', padding: '0', textAlign: 'center', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      <EditableField editMode={editMode} as="div" style={{ padding: '6px 10px', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        {qty}/Pcs
                      </EditableField>
                    </td>
                    <td style={{ border: '1px solid black', padding: '0', textAlign: 'center', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      <EditableField editMode={editMode} as="div" style={{ padding: '6px 10px', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        ${unitPrice.toFixed(3)}
                      </EditableField>
                    </td>
                    <td style={{ border: '1px solid black', padding: '0', textAlign: 'center', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      <EditableField editMode={editMode} as="div" style={{ padding: '6px 10px', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        ${formatNumberWithCommas(lineTotal.toFixed(2))}
                      </EditableField>
                    </td>
                  </tr>
                  );
                })
              );
            });

            return <React.Fragment key={orderIndex}>{orderRows}</React.Fragment>;
          })}
          <tr>
            <td colSpan={4} style={{ border: '1px solid black', padding: '0', textAlign: 'left', fontWeight: 'bold' }}>
              <EditableField editMode={editMode} as="div" style={{ padding: '6px 10px' }}>
                {netWeightFields.map((field, index) => (
                  <React.Fragment key={field.label}>
                    {field.label} {formatNumberWithCommas(field.value)}{field.suffix}
                    {index < netWeightFields.length - 1 ? ', ' : ''}
                  </React.Fragment>
                ))}
              </EditableField>
            </td>
            <td style={{ border: '1px solid black', padding: '6px 10px', textAlign: 'right' }}>
              <strong>TOTAL</strong>
            </td>
            <td style={{ border: '1px solid black', padding: '0', textAlign: 'center' }}>
              <strong>
                <EditableField editMode={editMode} style={{ display: 'inline-block' }}>
                  {grandQty}
                </EditableField>
              </strong>
            </td>
            <td style={{ border: '1px solid black', padding: '6px 10px', textAlign: 'center' }}></td>
            <td style={{ border: '1px solid black', padding: '0', textAlign: 'center' }}>
              <strong>
                <EditableField editMode={editMode} style={{ display: 'inline-block' }}>
                  ${formatNumberWithCommas(grandAmount.toFixed(2))}
                </EditableField>
              </strong>
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ fontSize: '13px', marginBottom: '30px' }}>
        <strong>TOTAL IN WORD:</strong>{' '}
        <EditableField editMode={editMode}>
          US DOLLAR ({convertNumberToWords(grandAmount).toUpperCase()} ONLY)
        </EditableField>
      </div>

      {/* Free-text clause area. Empty by default (renders as nothing in the
          printed/normal view); in Edit Content mode it's a clickable, editable
          box the client can type a clause into and re-edit later. */}
      <div style={{ textAlign: 'left', fontSize: '13px', marginBottom: '30px' }}>
        <EditableField
          editMode={editMode}
          as="div"
          style={{
            minHeight: editMode ? '24px' : undefined,
            whiteSpace: 'pre-wrap',
          }}
        />
      </div>

      <div style={{ textAlign: 'right', fontSize: '13px', marginTop: '30px', visibility: 'hidden' }}>
        For, <strong>
          <EditableField editMode={editMode} style={{ display: 'inline-block' }}>
            Miah and Sons Industries Limited
          </EditableField>
        </strong>
      </div>
      <div style={{ textAlign: 'right', fontSize: '13px', marginTop: '50px', visibility: 'hidden' }}>
        <strong>
          <EditableField editMode={editMode} style={{ display: 'inline-block' }}>
            Authorize Signature
          </EditableField>
        </strong>
      </div>
    </div>
  );
};

export default CommercialInvoice;